import type {LocationWithCoordinates} from "@/features/location-suggestions/utils/types";

/**
 * Minimum distance between pickup and dropoff in meters
 */
export const MINIMAL_STOP_DISTANCE = 100;

/**
 * Calculates spherical distance between two points in meters
 * Uses the haversine formula
 *
 * @param point1 - [latitude, longitude]
 * @param point2 - [latitude, longitude]
 * @returns Distance in meters
 */
export function calculateSphericalDistance(
    point1: [number, number],
    point2: [number, number]
): number {
    const toRadians = (deg: number) => (deg * Math.PI) / 180;

    const p1lat = toRadians(point1[0]);
    const p1lon = toRadians(point1[1]);
    const p2lat = toRadians(point2[0]);
    const p2lon = toRadians(point2[1]);

    const d = Math.acos(
        Math.sin(p1lat) * Math.sin(p2lat) +
            Math.cos(p1lat) * Math.cos(p2lat) * Math.cos(p2lon - p1lon)
    );

    return Math.round(d * 6378135);
}

/**
 * Checks if the distance between two locations meets the minimum required distance
 *
 * @param location1 - First location with coordinates
 * @param location2 - Second location with coordinates
 * @returns true if distance is >= MINIMAL_STOP_DISTANCE, false otherwise
 */
export function checkMinimalSphericalDistance(
    location1: LocationWithCoordinates,
    location2: LocationWithCoordinates
): boolean {
    const sphericalDistance = calculateSphericalDistance(
        [location1.lat, location1.lon],
        [location2.lat, location2.lon]
    );

    return sphericalDistance >= MINIMAL_STOP_DISTANCE;
}
